import { apiClient } from "@/lib/apiClient";
import type {
  Company,
  CompaniesListParams,
  CompaniesListResponse,
  CreateCompanyPayload,
  UpdateCompanyPayload,
} from "../types/companies.types";

const ENDPOINT = "/api/admin/companies";

function normalizeListBody(body: {
  data?: Company[] | { items?: Company[]; total?: number; count?: number };
  items?: Company[];
  total?: number;
  count?: number;
}): { list: Company[]; total: number } {
  let list: Company[] = [];
  if (Array.isArray(body?.data)) list = body.data;
  else if (
    body?.data &&
    typeof body.data === "object" &&
    "items" in body.data
  ) {
    const items = (body.data as { items?: Company[] }).items;
    list = Array.isArray(items) ? items : [];
  } else if (Array.isArray(body?.items)) list = body.items;

  let total = body?.total ?? body?.count ?? list.length;
  if (
    body?.data &&
    typeof body.data === "object" &&
    !Array.isArray(body.data)
  ) {
    const nested = body.data as { total?: number; count?: number };
    if (typeof nested.total === "number") total = nested.total;
    else if (typeof nested.count === "number") total = nested.count;
  }

  return { list, total: Number(total) };
}

export const companiesApi = {
  async getList(
    params: CompaniesListParams = {},
  ): Promise<CompaniesListResponse> {
    const { page = 1, limit = 20, search, sortBy, sortOrder } = params;
    const query: Record<string, unknown> = { page, limit };
    if (search?.trim()) query.search = search.trim();
    if (sortBy) query.sortBy = sortBy;
    if (sortOrder) query.sortOrder = sortOrder;

    const { data } = await apiClient.get(ENDPOINT, { params: query });
    const { list, total } = normalizeListBody(data);
    return { data: list, total };
  },

  async getById(id: string): Promise<Company> {
    const { data } = await apiClient.get<Company | { data?: Company }>(
      `${ENDPOINT}/${id}`,
    );
    const c = (data as { data?: Company })?.data ?? data;
    if (!c) throw new Error("Company not found");
    return c as Company;
  },

  async create(payload: CreateCompanyPayload): Promise<Company> {
    const body: Record<string, unknown> = { name: payload.name.trim() };
    if (payload.code?.trim()) body.code = payload.code.trim();
    const { data } = await apiClient.post<Company | { data?: Company }>(
      ENDPOINT,
      body,
    );
    const c = (data as { data?: Company })?.data ?? data;
    if (!c) throw new Error("Create failed");
    return c as Company;
  },

  async update(id: string, payload: UpdateCompanyPayload): Promise<Company> {
    const body: Record<string, unknown> = {};
    if (payload.name !== undefined) body.name = payload.name.trim();
    if (payload.code !== undefined)
      body.code = payload.code?.trim() ? payload.code.trim() : null;
    const { data } = await apiClient.patch<Company | { data?: Company }>(
      `${ENDPOINT}/${id}`,
      body,
    );
    const c = (data as { data?: Company })?.data ?? data;
    if (!c) throw new Error("Update failed");
    return c as Company;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${ENDPOINT}/${id}`);
  },
};
